import { createFileRoute, Link } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'

const heading: React.CSSProperties = { fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }
const updated: React.CSSProperties = { fontSize: 13, color: '#777', fontFamily: 'var(--oe-font-mono)', marginBottom: 40, fontWeight: 500 }
const h2: React.CSSProperties = { fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', marginTop: 48, marginBottom: 16 }
const h3: React.CSSProperties = { fontSize: 17, fontWeight: 700, marginTop: 32, marginBottom: 12 }
const p: React.CSSProperties = { fontSize: 16, lineHeight: 1.8, color: '#4a4745', marginBottom: 16, fontWeight: 500 }
const ul: React.CSSProperties = { fontSize: 16, lineHeight: 1.8, color: '#4a4745', marginBottom: 16, paddingLeft: 24, listStyleType: 'disc', fontWeight: 500 }
const li: React.CSSProperties = { marginBottom: 8 }

const tableWrapper: React.CSSProperties = { overflowX: 'auto', marginBottom: 24 }
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 14, lineHeight: 1.6 }
const th: React.CSSProperties = { textAlign: 'left', fontWeight: 700, padding: '10px 12px', borderBottom: '2px solid #e8e0d8', color: '#1a1216' }
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #e8e0d8', color: '#4a4745', fontWeight: 500 }

const lnk: React.CSSProperties = { color: '#1a1216', textDecoration: 'underline', textUnderlineOffset: 2 }

function SecurityPage() {
  return (
    <article>
      <Helmet>
        <title>Security | One-Email</title>
        <meta
          name="description"
          content="Learn about One-Email's security practices, infrastructure, encryption, and compliance. We own the full stack from API to SMTP delivery."
        />
        <link rel="canonical" href="https://one-email.com/security" />
      </Helmet>

      <h1 style={heading}>Security</h1>
      <p style={updated}>Last updated: March 24, 2026</p>

      <p style={p}>
        One-Email owns the full stack from API gateway to SMTP delivery. We encrypt everything, store
        nothing we don't need, and never fall back to third-party email relays. All persistent data
        is stored in the EU.
      </p>

      <h2 style={h2}>Our Approach</h2>
      <p style={p}>
        We built One-Email from the ground up — a custom Rust-based SMTP engine, not a wrapper around
        AWS SES or any other provider. This means we control every layer of the email delivery
        pipeline: API gateway, queue processing, message composition, DKIM signing, SMTP delivery,
        bounce handling, and complaint processing.
      </p>
      <p style={p}>
        Owning the full stack eliminates third-party trust boundaries that exist in SES-wrapper
        competitors. It also means we are solely responsible for security at every layer. We take
        that responsibility seriously.
      </p>

      <h2 style={h2}>Infrastructure Security</h2>
      <div style={tableWrapper}>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>Component</th>
              <th style={th}>Provider</th>
              <th style={th}>Security</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={td}>API Gateway</td>
              <td style={td}>Cloudflare Workers</td>
              <td style={td}>Global edge with built-in DDoS protection, TLS 1.3, WAF</td>
            </tr>
            <tr>
              <td style={td}>Database</td>
              <td style={td}>Neon (EU)</td>
              <td style={td}>Encrypted at rest, SOC 2 compliant provider, connection via Cloudflare Hyperdrive</td>
            </tr>
            <tr>
              <td style={td}>SMTP Engine</td>
              <td style={td}>Scaleway (Paris, EU)</td>
              <td style={td}>Custom Rust binary, encrypted disk, hardened server, SSH key-only access</td>
            </tr>
            <tr>
              <td style={td}>Object Storage</td>
              <td style={td}>Cloudflare R2</td>
              <td style={td}>Encrypted at rest, used for temporary attachments and templates</td>
            </tr>
            <tr>
              <td style={td}>Edge Cache</td>
              <td style={td}>Cloudflare KV</td>
              <td style={td}>Encrypted at rest, used for auth caching and suppression lists</td>
            </tr>
            <tr>
              <td style={td}>DNS</td>
              <td style={td}>Cloudflare</td>
              <td style={td}>DNSSEC support</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={p}>
        All persistent data (accounts, email metadata, API keys, DKIM keys, domains, suppression
        lists) is stored in the EU.
      </p>

      <h2 style={h2}>Encryption</h2>

      <h3 style={h3}>In Transit</h3>
      <ul style={ul}>
        <li style={li}>
          <strong>API and Dashboard:</strong> TLS 1.3 for all HTTPS connections
        </li>
        <li style={li}>
          <strong>SMTP Delivery:</strong> Opportunistic TLS for outbound email delivery, with
          MTA-STS enforcement where the recipient domain publishes a policy. We operate a dedicated
          MTA-STS policy worker for our own domains.
        </li>
        <li style={li}>
          <strong>Inter-service Communication:</strong> All internal communication between Workers,
          database, and SMTP engine uses HTTPS/TLS
        </li>
      </ul>

      <h3 style={h3}>At Rest</h3>
      <ul style={ul}>
        <li style={li}>
          <strong>Database:</strong> Neon Postgres encryption at rest
        </li>
        <li style={li}>
          <strong>SMTP Queue:</strong> Scaleway disk encryption at rest
        </li>
        <li style={li}>
          <strong>Object Storage:</strong> Cloudflare R2 encryption at rest
        </li>
        <li style={li}>
          <strong>DKIM Private Keys:</strong> Envelope encryption (AES-256-GCM). Each key is
          encrypted with a unique Data Encryption Key (DEK), which is itself encrypted with a Key
          Encryption Key (KEK) stored in Cloudflare Worker secrets — separate from the database. If
          the database is compromised, DKIM keys are unusable.
        </li>
      </ul>

      <h2 style={h2}>Email Authentication</h2>
      <ul style={ul}>
        <li style={li}>
          <strong>DKIM:</strong> Ed25519-SHA256 (primary, ~0.02ms per signature) with RSA-2048-SHA256
          (fallback for older receiving servers). Per-domain keys.
        </li>
        <li style={li}>
          <strong>SPF:</strong> Configured per customer domain
        </li>
        <li style={li}>
          <strong>DMARC:</strong> Full DMARC support with aggregate reporting
        </li>
        <li style={li}>
          <strong>MTA-STS:</strong> Dedicated Cloudflare Worker serves MTA-STS policies
        </li>
      </ul>

      <h2 style={h2}>API Key Security</h2>
      <ul style={ul}>
        <li style={li}>
          API keys are generated using cryptographically secure random bytes (Web Crypto API)
        </li>
        <li style={li}>
          Keys are <strong>shown once at creation</strong> and can never be retrieved again
        </li>
        <li style={li}>
          Only the <strong>SHA-256 hash</strong> is stored — we never store plaintext keys
        </li>
        <li style={li}>
          Two scopes available: <code>send</code> (email endpoints only) and <code>full</code> (all
          endpoints)
        </li>
        <li style={li}>
          Separate prefixes identify key type: <code>oe_live_sk_</code> (production) and{' '}
          <code>oe_test_sk_</code> (sandbox)
        </li>
        <li style={li}>
          Sandbox keys never send real emails — they route to a mock transport
        </li>
      </ul>

      <h2 style={h2}>Access Controls</h2>
      <ul style={ul}>
        <li style={li}>
          Dashboard authentication via Better Auth: magic link (primary), GitHub OAuth, or email +
          password
        </li>
        <li style={li}>
          Session cookies: <code>httpOnly</code>, <code>Secure</code>, <code>SameSite=None</code>,
          7-day expiry
        </li>
        <li style={li}>
          No plaintext secrets in source control — all sensitive configuration via Cloudflare Worker
          secrets
        </li>
        <li style={li}>
          Admin dashboard: separate OTP-based authentication, <code>SameSite=Strict</code> cookies,
          fully isolated from customer auth
        </li>
      </ul>

      <h2 style={h2}>Data Minimization</h2>
      <p style={p}>Our strongest security control is not having the data in the first place:</p>
      <ul style={ul}>
        <li style={li}>
          <strong>Email body content:</strong> Processed in memory, discarded immediately after
          delivery. We never store email bodies.
        </li>
        <li style={li}>
          <strong>Attachments:</strong> Stored temporarily during delivery (seconds to minutes),
          deleted after confirmation. Orphaned attachments purged daily.
        </li>
        <li style={li}>
          <strong>Subject lines:</strong> Stored as SHA-256 hashes — not human-readable.
        </li>
        <li style={li}>
          <strong>Credit card data:</strong> Never touches our servers. All billing through Polar.
        </li>
      </ul>

      <h2 style={h2}>Abuse Prevention</h2>
      <p style={p}>
        We operate a 4-layer abuse enforcement system to protect every customer's deliverability:
      </p>
      <ol
        style={{
          fontSize: 16,
          lineHeight: 1.8,
          color: '#4a4745',
          marginBottom: 16,
          paddingLeft: 24,
          fontWeight: 500,
        }}
      >
        <li style={li}>
          <strong>Ramp limits:</strong> New accounts start with restricted daily limits that increase
          over 3 weeks
        </li>
        <li style={li}>
          <strong>Automated monitoring:</strong> Continuous monitoring of bounce rates, complaint
          rates, and sending patterns. Auto-pause at &gt;5% bounce rate or &gt;0.1% complaint rate.
        </li>
        <li style={li}>
          <strong>Proactive signals:</strong> Pattern detection for high freemail ratio, low open
          rates, unusual volume spikes
        </li>
        <li style={li}>
          <strong>Manual review:</strong> Human review for flagged accounts and edge cases
        </li>
      </ol>
      <p style={p}>Additionally:</p>
      <ul style={ul}>
        <li style={li}>
          <strong>HMAC-signed attribution headers</strong> on every outbound email prevent spoofed
          ARF complaint attacks
        </li>
        <li style={li}>
          <strong>RFC 8058 one-click unsubscribe</strong> via a dedicated Cloudflare Worker with
          HMAC-signed tokens
        </li>
        <li style={li}>
          <strong>ARF complaint processing</strong> via a dedicated inbound SMTP listener
        </li>
        <li style={li}>
          <strong>DNSBL monitoring</strong> (hourly) and <strong>SNDS/Microsoft reputation
          monitoring</strong> (every 6 hours)
        </li>
        <li style={li}>
          <strong>Daily DNS verification</strong> for all active customer domains
        </li>
      </ul>

      <h2 style={h2}>Compliance</h2>

      <h3 style={h3}>Current</h3>
      <ul style={ul}>
        <li style={li}>
          <strong>GDPR compliant</strong> —{' '}
          <Link to="/legal/dpa" style={lnk}>
            Data Processing Agreement
          </Link>{' '}
          available, all infrastructure EU-hosted
        </li>
        <li style={li}>
          <strong>CAN-SPAM compliant</strong> — RFC 8058 one-click unsubscribe, abuse monitoring
        </li>
        <li style={li}>
          <strong>CASL compliant</strong> — Due diligence program for intermediary obligations
        </li>
        <li style={li}>
          <strong>UK GDPR</strong> — UK Addendum to SCCs included in DPA
        </li>
      </ul>

      <h3 style={h3}>Planned</h3>
      <ul style={ul}>
        <li style={li}>
          <strong>SOC 2 Type II</strong> — Planned. We are building our security program with SOC 2
          readiness in mind. Our infrastructure providers (Neon, Cloudflare) maintain SOC 2
          certifications.
        </li>
        <li style={li}>
          <strong>ISO 27001</strong> — Under evaluation for future implementation.
        </li>
      </ul>
      <p style={p}>
        We are transparent about what we have and what we don't. We will update this page as
        certifications are obtained.
      </p>

      <h2 style={h2}>Responsible Disclosure</h2>
      <p style={p}>
        We welcome security researchers to help us keep One-Email secure. If you discover a
        vulnerability, please report it responsibly. See our{' '}
        <Link to="/security/responsible-disclosure" style={lnk}>
          Responsible Disclosure Policy
        </Link>{' '}
        for details.
      </p>
      <p style={p}>
        <strong>Security contact:</strong>{' '}
        <a href="mailto:security@one-email.com" style={lnk}>
          security@one-email.com
        </a>
      </p>

      <h2 style={h2}>Payment Security</h2>
      <p style={p}>
        All payment processing is handled by Polar, which uses Stripe as its payment processor (PCI
        DSS Level 1 certified). One-Email never receives, processes, or stores credit card numbers,
        bank account details, or payment tokens.
      </p>

      <h2 style={h2}>Incident Response</h2>
      <p style={p}>In the event of a security incident:</p>
      <ul style={ul}>
        <li style={li}>
          We notify affected customers within <strong>48 hours</strong> of becoming aware of a
          personal data breach (per our{' '}
          <Link to="/legal/dpa" style={lnk}>
            DPA
          </Link>
          )
        </li>
        <li style={li}>We document all incidents regardless of severity</li>
        <li style={li}>We conduct post-incident reviews and implement preventive measures</li>
      </ul>

      <h2 style={h2}>Questions</h2>
      <p style={p}>
        For security inquiries, contact{' '}
        <a href="mailto:security@one-email.com" style={lnk}>
          <strong>security@one-email.com</strong>
        </a>
        .
      </p>
      <p style={p}>
        For privacy inquiries, contact{' '}
        <a href="mailto:privacy@one-email.com" style={lnk}>
          <strong>privacy@one-email.com</strong>
        </a>
        .
      </p>
    </article>
  )
}

export const Route = createFileRoute('/security/')({
  component: SecurityPage,
})
